import {useEffect, useState, useMemo, Profiler} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import assert from 'assert';
import {Layout, Header as LuffyHeader, Content} from '@baidu/luffy-comp-layout';
import {useHistory} from 'react-router';
import {tocRouters} from '@/config/routers';
import {RoutePageProps} from '@/interface';
import * as store from '@/store';
import {isSafari} from '@/config/env';
import {WizKnownError, ErrorCode} from '@/sdk/common/error';
import Notification from '@/components/Notification';
import {distanceFromNow} from '@/utils/dateUtils';
import {curUserIsAdmin} from '@/domains/space/Repository/knowledgeUtils';
import routers from '@/config/routers';
import TocError, {ErrorType} from '@/tocPages/components/Error';
import {
    EDITOR_MODE,
    RepositorySourceType,
    KNOWLEDGE_PATH_NAME,
    Role,
} from '@/Constant';
import classNames from 'classnames';
import {getRepositoryInfo, getDocumentInfo} from '@/services';
import kuPiper from '@/middleware/kuPiper';
import Header, {ISlots} from '../components/TocHeader';
import DocPage from '../DocPage';
import {SidebarContainerImpl} from '../components/Sidebar';

import {RepositoryContext} from './context';
import {perfLogger, PROFILER_KEY} from '@/buried/performance';
import {setDocumentTitle} from '@/utils/browserUtils';

// css
import layoutStyles from '../Layout/style.module.scss';
import styles from './index.module.scss';
import WaterMark from '@/components/WaterMark';

interface KnowledgeHomeProps extends RoutePageProps {
    curRepository?: Repository;
    curRepositoryError?: any;
    curDocumentError?: any;
    documentMode?: EDITOR_MODE;
    demoMode: boolean;
    docReady: boolean;
}

// eslint-disable-next-line max-statements
function KnowledgeHome(props: KnowledgeHomeProps) {
    const [hideWaterMark, setHideWaterMark] = useState(false);
    const {
        curRepository,
        match = {
            params: {
                spaceGuid: '',
                groupGuid: '',
                repositoryGuid: '',
                docGuid: '',
                tab: '',
            },
            path: '',
        },
        curRepositoryError,
        curDocumentError,
        documentMode,
        demoMode,
        docReady,
    } = props;

    const params = match.params;
    const docGuid = params.docGuid;
    const repoGuid = params.repositoryGuid;
    const intl = useIntl();
    const [error, setError] = useState<WizKnownError | null>(null);
    const [repository, setRepository] = useState<Repository | undefined>(undefined);
    const [reload, setReload] = useState(0);
    const [isRootZeroIndex, setIsRootZeroIndex] = useState(true);
    // mvp版本暂时不包含成员管理页，后续可能会有
    // const isShowMembers = docGuid === KNOWLEDGE_PATH_NAME.members || !!params.tab;
    const isDocPage = !!docGuid && !Object.values(KNOWLEDGE_PATH_NAME).includes(docGuid);
    // if (isShowMembers) {
    //     docGuid = undefined;
    // }

    // match到其他页面，重定向回首页
    const history = useHistory();
    if (KNOWLEDGE_PATH_NAME[docGuid as keyof typeof KNOWLEDGE_PATH_NAME]) {
        history.push(tocRouters.Welcome.path);
    }

    const [headerSlot, setHeaderSlot] = useState<ISlots | null>(null);

    useEffect(() => {
        return () => {
            store.setCurRepositoryError(null);
        };
    }, []);

    useEffect(() => {
        if (curRepositoryError && curRepositoryError.code) {
            setError(curRepositoryError);
            // 若是错误页面 清除水印
            setHideWaterMark(true);
        }
        if (!curRepositoryError) {
            setError(null);
        }
    }, [curRepositoryError]);

    useEffect(() => {
        // 第一次reload为0时 不刷新
        if (!reload) {
            return;
        }
        async function reloadRepo() {
            const {spaceGuid, groupGuid, repositoryGuid, docGuid} = params;
            if (!spaceGuid || !groupGuid || !repositoryGuid) {
                return;
            }
            // 由于私密知识库文档页面 若也没有文档权限时 错误展示页面其实是知识库的Apply组件
            // 所以刷新时 需要判断是否为文档页面
            // 若是文档页面 需要走文档获取信息逻辑
            if (docGuid) {
                getDocumentInfo({
                    spaceGuid,
                    groupGuid,
                    repositoryGuid,
                    docGuid,
                });
                return;
            }
            getRepositoryInfo({
                spaceGuid,
                groupGuid,
                repositoryGuid,
            });
        }
        reloadRepo();
    }, [params, reload]);

    useEffect(() => {
        const params = match.params;
        if (curRepository && curRepository.repositoryGuid === params.repositoryGuid) {
            setRepository(curRepository);
            store.setCurPage(routers.Knowledge.name, curRepository.repositoryGuid, docGuid);
        } else {
            setRepository(undefined);
            store.setCurPage(routers.Knowledge.name, params.repositoryGuid, docGuid);
        }
        if (curRepository?.sourceType === RepositorySourceType.notice) {
            setHideWaterMark(false);
        }
    }, [curRepository, match.params, docGuid]);

    useEffect(() => {
        kuPiper.runHook(kuPiper.hookNameMap.BeforeKnowledgeRenderHook, {repository});
    }, [repository]);

    useEffect(() => {
        const isFullScreenMode = demoMode || [EDITOR_MODE.history].includes(documentMode as EDITOR_MODE);
        if (isDocPage) {
            setIsRootZeroIndex(!isFullScreenMode);
        }
    }, [demoMode, docGuid, documentMode, isDocPage]);

    useEffect(() => {
        if (
            match.path.includes('/knowledge')
            && repoGuid === curRepository?.repositoryGuid
            && !isDocPage
        ) {
            setDocumentTitle(curRepository?.name, intl);
        }
    }, [curRepository, intl, isDocPage, match, repoGuid]);

    function handleReload() {
        setReload(Date.now());
    }

    function SidebarWrap() {
        // 目前私密库也会返回repository，但是在query接口docStatus和repoStatus都是403的场景不会给repository赋值
        if (!repository) {
            return null;
        }

        // 对外场景，受限库没有文档权限的情况下，且没有知识库的任何权限，不展示侧边栏
        // todo：后面增加互联网公开后，这里需要把公开的放出来
        // eslint-disable-next-line max-len
        if (curDocumentError?.code === ErrorCode.NoPermission && ![Role.Reader, Role.Member, Role.Admin, Role.Owner].includes(curRepository?.roleName || '')) {
            store.setTreeLoaded(true);
            return null;
        }

        return (
            <Profiler id={PROFILER_KEY.sideBar} onRender={perfLogger.compRender.onRender}>
                <SidebarContainerImpl
                    isPersonalKb={false}
                    isRecycleBin={false}
                    isSetting={false}
                    isHomeTag={false}
                    isMemberManage={false}
                    docGuid={params.docGuid}
                    repository={repository}
                />
            </Profiler>
        );
    }

    const getContent = kuPiper.runProxy(kuPiper.proxyNameMap.renderKnowledgeContent, (params: any) => {
        const {spaceGuid, groupGuid, repositoryGuid} = params;
        assert(spaceGuid);
        assert(groupGuid);
        assert(repositoryGuid);

        function getCodeType(code: string | number) {
            if (code === ErrorCode.NotFound) {
                return ErrorType.DocNotFound;
            } else if (code === ErrorCode.NoRepository) {
                return ErrorType.RepoNotFound;
            } else if (code === ErrorCode.NoPermission) {
                return ErrorType.RepoNoPermission;
            } else if (code === ErrorCode.tocNoRepoError) {
                return ErrorType.TocRepoNotFound;
            }
            return ErrorType.RepoRetry;
        }

        // isShowError场景：
        // 1、没有docGuid，说明停留在知识库页面。 treeloaded后，如果有可预览的文档会跳转第一篇文档，所以需要增加isTreeLoaded判断，避免页面闪烁
        // 2、没有repository，说明是私密库
        const isTreeLoaded = store.getTreeLoaded();
        const isShowError = (!docGuid && isTreeLoaded) || !repository;
        if (error && isShowError) {
            return <TocError
                type={getCodeType(error.code)}
                onRetry={handleReload}
            />;
        } else {
            return (
                repository && docGuid && (
                    <Profiler id={PROFILER_KEY.docHome} onRender={perfLogger.compRender.onRender}>
                        <DocPage
                            repository={repository}
                            docGuid={docGuid}
                            setHeaderSlot={setHeaderSlot}
                        />
                    </Profiler>
                )
            );
        }
    });

    function renderDeleteMessage() {
        if (!curRepository || !curRepository.deadline) {
            return null;
        }
        const isAdmin = curUserIsAdmin(curRepository);
        const deadline = distanceFromNow(curRepository?.deadline || 0);
        return (
            <Notification
                className={styles.notification}
                content={
                    isAdmin ? (
                        <FormattedMessage id={'warningAdminRepositoryWillBeDelete'} values={{deadline}} />
                    ) : (
                        <FormattedMessage id={'warningRepositoryWillBeDelete'} values={{deadline}} />
                    )
                }
                type="warning"
                closable
            />
        );
    }
    const contextValue = useMemo(() => ({docReady}), [docReady]);

    // start: fix safari bug 布局样式问题
    // https://console.cloud.baidu-int.com/devops/icafe/issue/so-kldoc-11269/show
    const [isFullScreen, setIsFullScreen] = useState(false);
    useEffect(() => {
        setTimeout(() => {
            setIsFullScreen(demoMode);
        }, 1000);
    }, [demoMode]);

    return (
        <Profiler id={PROFILER_KEY.knowledge} onRender={perfLogger.compRender.onRender}>
            <Layout
                className={classNames(
                    layoutStyles.layout, styles.layout,
                    {[styles.heightAuto]: isFullScreen && isSafari}
                )}
            >
                <RepositoryContext.Provider value={contextValue}>
                    <LuffyHeader id={'rlkbHeader'} className={classNames(layoutStyles.header, styles.headerWrap)}>
                        <Header headerSlot={headerSlot} />
                    </LuffyHeader>
                    <Content className={layoutStyles.content}>
                        <div
                            id="knowledgeHomeId"
                            className={classNames(styles.root, isRootZeroIndex && styles.rootZeroIndex)}
                        >
                            <WaterMark
                                docReady={docReady}
                                isDocPage={isDocPage}
                                repository={repository}
                                docGuid={docGuid}
                                hideWaterMark={hideWaterMark}
                                setHideWaterMark={setHideWaterMark}
                            />
                            <div
                                id="knowledgeContent"
                                className={classNames(styles.contentContainer)}
                            >
                                {renderDeleteMessage()}
                                {getContent(params)}
                            </div>
                            {SidebarWrap()}
                        </div>
                    </Content>
                </RepositoryContext.Provider>
            </Layout>
        </Profiler>
    );
}

const KnowledgeHomeImpl = store.connect([
    store.KEYS.CUR_REPOSITORY,
    store.KEYS.CUR_REPOSITORY_ERROR,
    store.KEYS.CUR_DOCUMENT_ERROR,
    store.KEYS.DOCUMENT_MODE,
    store.KEYS.DEMO_MODE,
    store.KEYS.DOC_READY,
    store.KEYS.IS_TREE_LOADED,
])(KnowledgeHome);

export default KnowledgeHomeImpl;
